import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  X, 
  BarChart3, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Bell,
  FileText,
  Eye,
  EyeOff,
  Clock
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSalesData } from '../features/sales/hooks/useSalesData.js';
import { useExpensesDomain } from '../features/expenses/hooks/useExpensesDomain.js';
import { useSupermarketPaymentsDomain } from '../features/payments/hooks/useSupermarketPaymentsDomain.js';
import { useCashRegisterDomain } from '../features/cashRegister/hooks/useCashRegisterDomain.js';
import { useOperationsDomain } from '../features/operations/hooks/useOperationsDomain.js';
import { useRole } from '../contexts/RoleContext';
import useFormatCurrency from '../hooks/useFormatCurrency';
import {
  getDefaultWorkerDashboard,
  getWorkerDashboard,
  isReadModelReady
} from '../services/cacheReadModel';
import { useDomainCacheBump } from '../hooks/useDomainCacheBump';
import { useDomainOperationsListener } from '../hooks/useDomainInventoryListener';
import '../styles/components/worker-dashboard-modal.css';
import AdsModalPortal from './ads/AdsModalPortal';
import { usePresenceTransition } from '../hooks/usePresenceTransition';
import { toSafeDate, formatSafeDate, formatSafeDateTime } from '../utils/formatSafety';
import { getCurrentBusinessId } from '../utils/sharedCore';
import { enrichSessionsWithVentes } from '../utils/sessionSalesSync';
import {
  normalizePaymentMethodForSession,
  normalizeSaleTotal,
  sumElectronicSalesByProvider
} from '../utils/saleSessionHelpers';
import { getSaleTotalAmount } from '../domain/restaurant/restaurantDishSaleService';
import {
  computeSessionExpectedFromWorkerSession,
  sessionFlowToBreakdownDeviation
} from '../domain/finance/sessionCashflow';
import CashExpectedBreakdown from './cash-register/CashExpectedBreakdown';
import { getEcartBreakdownLabels } from '../utils/ecartBreakdownLabels';
import { getPaymentMethodName } from '../services/paymentMethodService';

const WorkerDashboardModal = ({ isOpen, onClose, workerId }) => {
  const { currentLanguage, isRTL, t } = useLanguage();
  const te = (key) => t(`workerDashboardModal.${key}`);
  const displayLocale = isRTL ? 'ar-MR' : 'fr-MR';
  const formatCurrency = useFormatCurrency();
  const { sales } = useSalesData();
  const { discrepancyManagement } = useOperationsDomain();
  const { expenses } = useExpensesDomain();
  const { supermarketPayments } = useSupermarketPaymentsDomain();
  const { invoicePayments } = useCashRegisterDomain();
  const { currentRole } = useRole();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [showToleranceStatus, setShowToleranceStatus] = useState(false);
  const [sessionFilter, setSessionFilter] = useState('all'); // 'all', 'today', 'week', 'month'

  // Force re-render when cache is updated (same as Dashboard and Reports)
  const [cacheUpdateTrigger, setCacheUpdateTrigger] = useState(0);

  const bumpCacheTrigger = useCallback(() => {
    setCacheUpdateTrigger((prev) => prev + 1);
  }, []);

  useDomainCacheBump(bumpCacheTrigger, {
    types: [
      'cache_updated',
      'inventory_updated',
      'products_updated',
      'data_changed',
      'sales_updated',
      'worker_session_updated'
    ]
  });

  // Get current business ID
  const currentBusinessId = useMemo(() => getCurrentBusinessId(), []);

  // Get worker dashboard data from instant cache (same approach as Dashboard and Reports)
  const workerDashboardData = useMemo(() => {
    if (!isReadModelReady()) {
      return getDefaultWorkerDashboard();
    }
    if (!workerId) return getDefaultWorkerDashboard();
    return getWorkerDashboard(workerId, currentBusinessId);
    // sales/discrepancyManagement/cacheUpdateTrigger are intentional recompute
    // triggers so the cache read re-runs when domain data bumps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, currentBusinessId, sales, discrepancyManagement, cacheUpdateTrigger]);

  useDomainOperationsListener({ onWorkerSession: bumpCacheTrigger }, [bumpCacheTrigger]);

  // Rebuild worker dashboard tip when opening Mes rapports (D09 — warmWorker path).
  useEffect(() => {
    if (!isOpen || !workerId) return undefined;
    let cancelled = false;
    void import('../services/cacheReadModel').then(({ ensureWarm, READ_MODELS }) => {
      if (cancelled) return;
      ensureWarm(READ_MODELS.WORKER_DASHBOARD, {
        force: true,
        priority: 'idle',
        idleTimeout: 2500,
        data: {
          sales,
          expenses,
          supermarketPayments,
          invoicePayments,
          discrepancyManagement
        },
        onDone: () => {
          if (!cancelled) bumpCacheTrigger();
        }
      });
    });
    return () => {
      cancelled = true;
    };
    // Intentionally only on open / worker change — sales bumps already refresh via domain.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open-time warm only
  }, [isOpen, workerId, bumpCacheTrigger]);

  // Get text based on language

  const isRtl = isRTL;

  const deviationStatusClass = (status) => {
    if (status === 'accepted') return 'wdm-deviation__status--accepted';
    if (status === 'rejected') return 'wdm-deviation__status--rejected';
    return 'wdm-deviation__status--pending';
  };

  const deviationStatusLabel = (status) => {
    if (status === 'accepted') return te('statusAccepted');
    if (status === 'rejected') return te('statusRejected');
    return te('statusPending');
  };
  const formatDateTimeLocal = (value) => {
    const safeDate = toSafeDate(value);
    if (!safeDate) return '-';
    return formatSafeDateTime(safeDate, displayLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, '-');
  };

  // Get worker's sales data from cache
  const workerSales = workerDashboardData?.workerSales || [];
  
  // Get worker's sessions from cache
  const workerSessions = workerDashboardData?.workerSessions || [];

  const sessionFlowContext = useMemo(
    () => ({
      expenses: expenses || [],
      supermarketPayments: supermarketPayments || [],
      invoicePayments: invoicePayments || [],
      allSales: sales || []
    }),
    [expenses, supermarketPayments, invoicePayments, sales]
  );

  const enrichedWorkerSessions = useMemo(() => {
    if (!currentBusinessId || !workerSessions.length) return workerSessions;
    return enrichSessionsWithVentes(workerSessions, currentBusinessId);
    // sales/cacheUpdateTrigger are intentional recompute triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerSessions, currentBusinessId, sales, cacheUpdateTrigger]);

  const getSessionFlow = useCallback(
    (session) => computeSessionExpectedFromWorkerSession(session, sessionFlowContext),
    [sessionFlowContext]
  );

  const isInCurrentMonth = useCallback((dateValue) => {
    const d = toSafeDate(dateValue);
    if (!d) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }, []);

  const buildPeriodStats = useCallback(
    (sessions) => {
      const sessionSales = sessions.flatMap((session) =>
        Array.isArray(session.sales) ? session.sales : []
      );
      let cashCount = 0;
      let electronicCount = 0;
      sessionSales.forEach((sale) => {
        const method = normalizePaymentMethodForSession(sale);
        if (method === 'cash') cashCount += 1;
        else electronicCount += 1;
      });

      const totalAmount = sessionSales.reduce((sum, sale) => sum + normalizeSaleTotal(sale), 0);
      const totalCash = sessions.reduce((sum, session) => sum + getSessionFlow(session).cashSales, 0);
      const totalElectronic = sessions.reduce(
        (sum, session) => sum + getSessionFlow(session).electronicSales,
        0
      );
      const providersMap = sumElectronicSalesByProvider(sessionSales);
      const electronicProviders = Object.entries(providersMap)
        .map(([name, amount]) => ({
          name,
          totalAmount: Number(amount) || 0,
          count: sessionSales.filter((sale) => normalizePaymentMethodForSession(sale) === name).length
        }))
        .sort((a, b) => b.totalAmount - a.totalAmount);

      return {
        totalAmount,
        totalCash,
        totalElectronic,
        sessionCount: sessions.length,
        salesCount: sessionSales.length,
        cashCount,
        electronicCount,
        averagePerSession: sessions.length > 0 ? totalAmount / sessions.length : 0,
        averagePerSale: sessionSales.length > 0 ? totalAmount / sessionSales.length : 0,
        cashPercentage: totalAmount > 0 ? Number(((totalCash / totalAmount) * 100).toFixed(1)) : 0,
        electronicPercentage: totalAmount > 0 ? Number(((totalElectronic / totalAmount) * 100).toFixed(1)) : 0,
        electronicProviders
      };
    },
    [getSessionFlow]
  );

  const monthStats = useMemo(() => {
    const monthSessions = enrichedWorkerSessions.filter((session) =>
      isInCurrentMonth(session.loginTime)
    );
    return buildPeriodStats(monthSessions);
  }, [enrichedWorkerSessions, isInCurrentMonth, buildPeriodStats]);

  // Filter sessions based on selected filter
  const filteredSessions = useMemo(() => {
    if (!enrichedWorkerSessions.length) return [];
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    return enrichedWorkerSessions.filter(session => {
      const sessionDate = toSafeDate(session.loginTime);
      if (!sessionDate) return false;
      
      switch (sessionFilter) {
        case 'today':
          return sessionDate >= today;
        case 'week':
          return sessionDate >= weekAgo;
        case 'month':
          return sessionDate >= monthAgo;
        default:
          return true;
      }
    }).sort((a, b) => (toSafeDate(b.loginTime)?.getTime() ?? 0) - (toSafeDate(a.loginTime)?.getTime() ?? 0));
  }, [enrichedWorkerSessions, sessionFilter]);

  const reportStats = useMemo(
    () => buildPeriodStats(filteredSessions),
    [filteredSessions, buildPeriodStats]
  );

  // Get worker's deviations from cache
  const workerEcarts = workerDashboardData?.workerEcarts || {
    selfDeviations: [],
    handoverDeviations: [],
    totalCount: 0,
    openCount: 0
  };
  
  const workerDeviations = {
    self: workerEcarts.selfDeviations || [],
    handover: workerEcarts.handoverDeviations || [],
    total: workerEcarts.totalCount || 0,
    open: workerEcarts.openCount || 0
  };

  // Get worker's notifications (simplified)
  const workerNotifications = [];

  // Get tolerance status (simplified)
  const toleranceStatus = {
    status: 'normal',
    message: 'Toleranz im normalen Bereich',
    percentage: 0,
    usedAmount: 0,
    monthlyLimit: 0
  };

  const { present, closing } = usePresenceTransition(Boolean(isOpen));
  if (!present) return null;

  return (
    <AdsModalPortal open={Boolean(isOpen)} onRouteDismiss={onClose}>
    <div
      className={`wdm-overlay${closing ? ' ads-modal-exiting' : ' ads-modal-entering'}`}
      dir={isRtl ? 'rtl' : 'ltr'}
      data-ads-modal-state={closing ? 'closing' : 'open'}
    >
      <div className="wdm-shell">
        {/* Header */}
        <div className="wdm-header">
          <div>
            <h2 className="wdm-header__title">
              {te('title')}
            </h2>
            <p className="wdm-header__subtitle">
              {te('hello')} {currentRole?.name || 'Worker'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="wdm-close-btn"
            aria-label={te('close')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="wdm-tabs">
          {[
            { id: 'overview', label: te('tabOverview'), icon: BarChart3 },
            { id: 'active-session', label: te('tabActiveSession'), icon: Clock },
            { id: 'reports', label: te('tabReports'), icon: FileText },
            { id: 'notifications', label: te('tabNotifications'), icon: Bell },
            { id: 'deviations', label: te('tabDeviations'), icon: AlertTriangle }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`wdm-tab${activeTab === tab.id ? ' wdm-tab--active' : ''}`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="wdm-content">
          {activeTab === 'overview' && (
            <div>
              {/* Welcome Header */}
              <div className="wdm-welcome">
                <h2 className="wdm-welcome__title">
                  {te('bienvenueDansVotreTableauBord')}
                </h2>
                <p className="wdm-welcome__subtitle">
                  {te('gerezVosVentesSuivezVos')}
                </p>
              </div>

              {/* Résumé tabulaire — mois en cours, calcul système */}
              <div className="wdm-section-block">
                <h3 className="wdm-heading-lg">{te('monthSummaryTitle')}</h3>
                <div className="wdm-table-wrap">
                  <table className="wdm-table wdm-table--md">
                    <thead>
                      <tr className="wdm-table__head-row">
                        <th className="wdm-table__th wdm-table__th--start">{te('columnIndicator')}</th>
                        <th className="wdm-table__th wdm-table__th--num">{te('columnCount')}</th>
                        <th className="wdm-table__th wdm-table__th--num">{te('columnAmount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="wdm-table__cell">{te('sessions')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{monthStats.sessionCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">—</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('totalVentes')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{monthStats.salesCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">{formatCurrency(monthStats.totalAmount)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('totalEspeces')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{monthStats.cashCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">{formatCurrency(monthStats.totalCash)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('totalElectronique2')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{monthStats.electronicCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">{formatCurrency(monthStats.totalElectronic)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('moyenneSession')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">—</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(monthStats.averagePerSession)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('avgPerSale')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">—</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(monthStats.averagePerSale)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('tabDeviations')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{workerEcarts.totalCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">
                          {workerEcarts.openCount > 0 ? `${workerEcarts.openCount} ${te('statusPending').toLowerCase()}` : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {monthStats.electronicProviders && monthStats.electronicProviders.length > 0 && (
                <div className="wdm-section-block">
                  <h3 className="wdm-heading-lg">
                    {te('detailPaiementsElectroniques')}
                  </h3>
                  <div className="wdm-table-wrap">
                    <table className="wdm-table wdm-table--lg">
                      <thead>
                        <tr className="wdm-table__head-row">
                          <th className="wdm-table__th wdm-table__th--start">{te('provider')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('transactions')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('amount')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('part')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthStats.electronicProviders.map((provider) => {
                          const providerKey = String(provider.name || '').toLowerCase();
                          const displayName = providerKey === 'electronic'
                            ? te('unspecified')
                            : getPaymentMethodName(providerKey, currentLanguage);
                          const share = monthStats.totalElectronic > 0
                            ? ((provider.totalAmount / monthStats.totalElectronic) * 100).toFixed(1)
                            : '0.0';
                          return (
                            <tr key={provider.name}>
                              <td className="wdm-table__cell">{displayName}</td>
                              <td className="wdm-table__cell wdm-table__cell--num">{provider.count}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">{formatCurrency(provider.totalAmount)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num">{share}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="wdm-table__cell wdm-table__cell--heavy">{te('totalPaiementsElectroniques')}</td>
                          <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">
                            {monthStats.electronicCount}
                          </td>
                          <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount wdm-table__cell--bold">
                            {formatCurrency(monthStats.totalElectronic)}
                          </td>
                          <td className="wdm-table__cell wdm-table__cell--num">{monthStats.electronicPercentage}%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Tolerance Status (Hidden by default) */}
              <div className="wdm-tolerance">
                <div className="wdm-tolerance__header">
                  <h3 className="wdm-tolerance__title">
                    {te('statutEvaluation')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowToleranceStatus(!showToleranceStatus)}
                    className="wdm-toggle-btn"
                  >
                    {showToleranceStatus ? <EyeOff size={14} /> : <Eye size={14} />}
                    {showToleranceStatus ?
                      te('masquer') :
                      te('afficher')
                    }
                  </button>
                </div>

                {showToleranceStatus && (
                  <div className="wdm-tolerance__body">
                    <div className="wdm-tolerance__status-row">
                      {toleranceStatus.status === 'critical' && (
                        <XCircle size={20} className="wdm-icon--red" />
                      )}
                      {toleranceStatus.status === 'warning' && (
                        <AlertTriangle size={20} className="wdm-icon--amber" />
                      )}
                      {toleranceStatus.status === 'normal' && (
                        <CheckCircle size={20} className="wdm-icon--green" />
                      )}
                      <span className={`wdm-tolerance__message wdm-tolerance__message--${toleranceStatus.status}`}>
                        {toleranceStatus.message}
                      </span>
                    </div>

                    {toleranceStatus.usedAmount !== undefined && (
                      <div className="wdm-tolerance__usage">
                        <div className="wdm-tolerance__usage-row">
                          <span>{te('utilise')}: {formatCurrency(toleranceStatus.usedAmount)}</span>
                          <span>{te('limite')}: {formatCurrency(toleranceStatus.monthlyLimit)}</span>
                        </div>
                        <progress
                          className={`wdm-progress wdm-progress--${toleranceStatus.status}`}
                          value={toleranceStatus.percentage}
                          max={100}
                        />
                        <div className="wdm-progress__label">
                          {toleranceStatus.percentage.toFixed(1)}%
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'active-session' && (
            <div>
              <h3 className="wdm-section-title">
                {te('maSessionActive')}
              </h3>

              {/* Active Session Info */}
              {(() => {
                const activeSession = enrichedWorkerSessions.find(session => !session.logoutTime);

                if (!activeSession) {
                  return (
                    <div className="wdm-empty-session">
                      <Clock size={48} className="wdm-icon--center wdm-icon--muted" />
                      <h4 className="wdm-empty-session__title">
                        {te('aucuneSessionActive')}
                      </h4>
                      <p className="wdm-empty-session__text">
                        {te('vousNAvezPasEncore')}
                      </p>
                    </div>
                  );
                }

                const startTime = toSafeDate(activeSession.loginTime);
                const now = new Date();
                const duration = startTime ? Math.round((now - startTime) / (1000 * 60)) : 0;
                const hours = Math.floor(duration / 60);
                const minutes = duration % 60;
                const sessionSales = Array.isArray(activeSession.sales) ? activeSession.sales : [];
                const sessionSalesCount = sessionSales.length;
                const initialCashAmount = Number(activeSession.initialAmount || activeSession.cashAmount || 0);
                const initialElectronicAmount = Number(activeSession.initialElectronicAmount || activeSession.electronicAmount || 0);
                const sessionFlow = getSessionFlow(activeSession);
                const sessionCashSales = sessionFlow.cashSales;
                const sessionElectronicSales = sessionFlow.electronicSales;
                const sessionCashDebt = Number(sessionFlow.cashDebtCollections) || 0;
                const sessionElectronicDebt = Number(sessionFlow.electronicDebtCollections) || 0;
                const sessionCashOutgoing = sessionFlow.cashOutgoing;
                const sessionElectronicOutgoing = sessionFlow.electronicOutgoing;
                const sessionElectronicByProvider = sumElectronicSalesByProvider(sessionSales);
                const expectedCashAmount = sessionFlow.expectedCash;
                const expectedElectronicAmount = sessionFlow.expectedElectronic;
                const sessionTotalSales = sessionSales.reduce((sum, sale) => sum + normalizeSaleTotal(sale), 0);

                return (
                  <div>
                    <div className="wdm-active-banner">
                      <div className="wdm-active-banner__header">
                        <Clock size={24} className="wdm-icon--white-mr" />
                        <h4 className="wdm-active-banner__title">
                          {te('tabActiveSession')}
                        </h4>
                      </div>
                      <p className="wdm-active-banner__meta">
                        {te('commencee')}: {formatDateTimeLocal(startTime)}
                      </p>
                      <p className="wdm-active-banner__meta wdm-active-banner__meta--spaced">
                        {te('duree')}: {hours > 0 ? `${hours}h ` : ''}{minutes}m
                      </p>
                    </div>

                    <div className="wdm-section-block">
                      <h4 className="wdm-subsection-title">
                        {te('resumeSession')}
                      </h4>
                      <div className="wdm-table-wrap">
                        <table className="wdm-table wdm-table--md">
                          <thead>
                            <tr className="wdm-table__head-row">
                              <th className="wdm-table__th wdm-table__th--start">{te('indicateur')}</th>
                              <th className="wdm-table__th wdm-table__th--num">{te('cash')}</th>
                              <th className="wdm-table__th wdm-table__th--num">{te('electronic')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="wdm-table__cell">{te('debutSession')}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(initialCashAmount)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(initialElectronicAmount)}</td>
                            </tr>
                            <tr>
                              <td className="wdm-table__cell">{te('salesCount')}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold" colSpan={2}>{sessionSalesCount}</td>
                            </tr>
                            <tr>
                              <td className="wdm-table__cell">{te('ventesSession')}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(sessionCashSales)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(sessionElectronicSales)}</td>
                            </tr>
                            {(Math.abs(sessionCashDebt) > 0.001 || Math.abs(sessionElectronicDebt) > 0.001) ? (
                              <tr>
                                <td className="wdm-table__cell">{te('debtCollectionsSession')}</td>
                                <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(sessionCashDebt)}</td>
                                <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(sessionElectronicDebt)}</td>
                              </tr>
                            ) : null}
                            <tr>
                              <td className="wdm-table__cell">{te('salesTotal')}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount" colSpan={2}>{formatCurrency(sessionTotalSales)}</td>
                            </tr>
                            <tr>
                              <td className="wdm-table__cell">{te('sortiesSession')}</td>
                              <td className="wdm-table__cell wdm-table__cell--warn">−{formatCurrency(sessionCashOutgoing)}</td>
                              <td className="wdm-table__cell wdm-table__cell--warn">−{formatCurrency(sessionElectronicOutgoing)}</td>
                            </tr>
                            <tr>
                              <td className="wdm-table__cell wdm-table__cell--heavy">{te('expected')}</td>
                              <td className="wdm-table__cell wdm-table__cell--positive">{formatCurrency(expectedCashAmount)}</td>
                              <td className="wdm-table__cell wdm-table__cell--positive-blue">{formatCurrency(expectedElectronicAmount)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <CashExpectedBreakdown
                        deviation={sessionFlowToBreakdownDeviation(activeSession, sessionFlow)}
                        formatAmount={formatCurrency}
                        labels={getEcartBreakdownLabels(currentLanguage)}
                        showElectronic
                      />
                    </div>

                    <div className="wdm-section-block">
                      <h4 className="wdm-subsection-title">
                        {te('electroniqueFournisseur')}
                      </h4>
                      <div className="wdm-table-wrap">
                        <table className="wdm-table wdm-table--sm">
                          <thead>
                            <tr className="wdm-table__head-row">
                              <th className="wdm-table__th wdm-table__th--start">{te('provider')}</th>
                              <th className="wdm-table__th wdm-table__th--num">{te('transactions')}</th>
                              <th className="wdm-table__th wdm-table__th--num">{te('amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.keys(sessionElectronicByProvider).length === 0 ? (
                              <tr>
                                <td colSpan={3} className="wdm-table__cell wdm-table__cell--empty">
                                  {te('aucuneTransactionElectroniqueMoment')}
                                </td>
                              </tr>
                            ) : (
                              Object.entries(sessionElectronicByProvider).map(([provider, amount]) => {
                                const providerCount = sessionSales.filter(
                                  (sale) => normalizePaymentMethodForSession(sale) === provider
                                ).length;
                                return (
                                  <tr key={provider}>
                                    <td className="wdm-table__cell">
                                      {provider === 'electronic'
                                        ? te('unspecified')
                                        : getPaymentMethodName(provider, currentLanguage)}
                                    </td>
                                    <td className="wdm-table__cell wdm-table__cell--num">{providerCount}</td>
                                    <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{formatCurrency(amount)}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {sessionSales.length > 0 && (
                      <div className="wdm-section-block">
                        <h4 className="wdm-subsection-title">
                          {te('ventesDansCetteSession')} ({sessionSalesCount})
                        </h4>
                        <div className="wdm-table-wrap wdm-table-wrap--scroll">
                          <table className="wdm-table wdm-table--lg">
                            <thead>
                              <tr className="wdm-table__head-row">
                                <th className="wdm-table__th wdm-table__th--start">#</th>
                                <th className="wdm-table__th wdm-table__th--start">{te('date')}</th>
                                <th className="wdm-table__th wdm-table__th--start">{te('modePaiement')}</th>
                                <th className="wdm-table__th wdm-table__th--num">{te('amount')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...sessionSales].reverse().map((sale, index) => {
                                const normalizedMethod = normalizePaymentMethodForSession(sale);
                                const providerLabel = normalizedMethod === 'cash'
                                  ? te('cash')
                                  : normalizedMethod === 'electronic'
                                    ? `${te('electronic')} - ${te('unspecified')}`
                                    : `${te('electronic')} - ${getPaymentMethodName(normalizedMethod, currentLanguage)}`;
                                return (
                                  <tr key={sale.id || index}>
                                    <td className="wdm-table__cell">{sessionSalesCount - index}</td>
                                    <td className="wdm-table__cell wdm-table__cell--muted">
                                      {formatSafeDate(sale.date, displayLocale, {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }, '-')}
                                    </td>
                                    <td className="wdm-table__cell">{providerLabel}</td>
                                    <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">
                                      {formatCurrency(normalizeSaleTotal(sale))}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="wdm-session-actions">
                      <p className="wdm-session-actions__text">
                        {te('terminerCetteSessionUtilisezBouton')}
                      </p>
                      <div className="wdm-session-actions__hint">
                        {te('toutesDonneesSessionSerontSauvegardees')}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'reports' && (
            <div>
              <h3 className="wdm-section-title">
                {te('mesRapportsPersonnels')}
              </h3>

              <div className="wdm-filter-panel">
                <h4 className="wdm-filter-panel__title">
                  {te('filtrerSessions')}
                </h4>
                <div className="wdm-filter-row">
                  {[
                    { key: 'all', label: te('toutes') },
                    { key: 'today', label: te('aujourdHui') },
                    { key: 'week', label: te('cetteSemaine') },
                    { key: 'month', label: te('ceMois') }
                  ].map(filter => (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => setSessionFilter(filter.key)}
                      className={`wdm-filter-btn${sessionFilter === filter.key ? ' wdm-filter-btn--active' : ''}`}
                    >
                      {filter.label} ({filteredSessions.length})
                    </button>
                  ))}
                </div>
              </div>

              <div className="wdm-section-block">
                <h4 className="wdm-subsection-title">
                  {te('periodSummaryTitle')}
                </h4>
                <div className="wdm-table-wrap">
                  <table className="wdm-table wdm-table--md">
                    <thead>
                      <tr className="wdm-table__head-row">
                        <th className="wdm-table__th wdm-table__th--start">{te('columnIndicator')}</th>
                        <th className="wdm-table__th wdm-table__th--num">{te('columnCount')}</th>
                        <th className="wdm-table__th wdm-table__th--num">{te('columnAmount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="wdm-table__cell">{te('sessions')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{reportStats.sessionCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">—</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('totalVentes')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{reportStats.salesCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">{formatCurrency(reportStats.totalAmount)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('totalEspeces')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{reportStats.cashCount}</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(reportStats.totalCash)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('moyenneSession')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">—</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(reportStats.averagePerSession)}</td>
                      </tr>
                      <tr>
                        <td className="wdm-table__cell">{te('especes')}</td>
                        <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--muted">—</td>
                        <td className="wdm-table__cell wdm-table__cell--num">{reportStats.cashPercentage}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="wdm-section-block">
                <h4 className="wdm-subsection-title">
                  {te('sessionsTravail')}
                </h4>
                <div className="wdm-table-wrap">
                  {filteredSessions.length > 0 ? (
                    <table className="wdm-table wdm-table--2xl">
                      <thead>
                        <tr className="wdm-table__head-row">
                          <th className="wdm-table__th wdm-table__th--start">{te('session')}</th>
                          <th className="wdm-table__th wdm-table__th--start">{te('periode')}</th>
                          <th className="wdm-table__th wdm-table__th--center">{te('status')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('debutEspeces')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('finEspeces')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('salesCount')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('salesAmount')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('expected')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSessions.map((session, index) => {
                          const startTime = toSafeDate(session.loginTime);
                          const endTime = session.logoutTime ? toSafeDate(session.logoutTime) : null;
                          const sessionSalesList = Array.isArray(session.sales) ? session.sales : [];
                          const sessionFlow = getSessionFlow(session);
                          const sessionAmount = sessionSalesList.reduce(
                            (sum, sale) => sum + normalizeSaleTotal(sale),
                            0
                          );
                          const finalCash = session.logoutTime
                            ? Number(session.finalCashAmount || session.cashAmount || 0)
                            : Number(session.initialAmount || session.cashAmount || 0);
                          return (
                            <tr key={session.id || index} className="wdm-table__row">
                              <td className="wdm-table__cell wdm-table__cell--start wdm-table__cell--bold">
                                {te('session')} #{filteredSessions.length - index}
                              </td>
                              <td className="wdm-table__cell wdm-table__cell--start wdm-table__cell--muted">
                                {startTime ? formatDateTimeLocal(startTime) : '-'}
                                {endTime ? ` - ${formatDateTimeLocal(endTime)}` : ` - ${te('cours')}`}
                              </td>
                              <td className="wdm-table__cell wdm-table__cell--center wdm-table__cell--muted">
                                {session.logoutTime ? te('terminee') : te('statusActive')}
                              </td>
                              <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(session.initialAmount || 0)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(finalCash)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--bold">{sessionSalesList.length}</td>
                              <td className="wdm-table__cell wdm-table__cell--num wdm-table__cell--amount">{formatCurrency(sessionAmount)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num">{formatCurrency(sessionFlow.expectedCash)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="wdm-table-empty">
                      {te('aucuneSessionTrouvee')}
                    </div>
                  )}
                </div>
              </div>

              <div className="wdm-section-block">
                <h4 className="wdm-subsection-title">
                  {te('ventesRecentes')}
                </h4>
                {workerSales.length > 0 ? (
                  <div className="wdm-table-wrap wdm-table-wrap--scroll">
                    <table className="wdm-table wdm-table--lg">
                      <thead>
                        <tr className="wdm-table__head-row">
                          <th className="wdm-table__th wdm-table__th--start">{te('reference')}</th>
                          <th className="wdm-table__th wdm-table__th--start">{te('date')}</th>
                          <th className="wdm-table__th wdm-table__th--start">{te('modePaiement')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('amount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workerSales.slice(0, 10).map((sale) => {
                          const normalizedMethod = normalizePaymentMethodForSession(sale);
                          const providerLabel = normalizedMethod === 'cash'
                            ? te('cash')
                            : normalizedMethod === 'electronic'
                              ? `${te('electronic')} - ${te('unspecified')}`
                              : `${te('electronic')} - ${getPaymentMethodName(normalizedMethod, currentLanguage)}`;
                          return (
                            <tr key={sale.id} className="wdm-table__row">
                              <td className="wdm-table__cell wdm-table__cell--start">{sale.orderId || `#${sale.id}`}</td>
                              <td className="wdm-table__cell wdm-table__cell--start wdm-table__cell--muted">{formatDateTimeLocal(sale.date)}</td>
                              <td className="wdm-table__cell wdm-table__cell--start">{providerLabel}</td>
                              <td className="wdm-table__cell wdm-table__cell--amount">{formatCurrency(getSaleTotalAmount(sale))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="wdm-empty-state">
                    <FileText size={48} className="wdm-icon--empty" />
                    <p>{te('aucuneVenteRecente')}</p>
                  </div>
                )}
              </div>

              {reportStats.electronicProviders && reportStats.electronicProviders.length > 0 && (
                <div>
                  <h4 className="wdm-subsection-title">
                    {te('analyseDetailleeMethodesPaiementElectroniques')}
                  </h4>

                  <div className="wdm-summary-card">
                    <div className="wdm-panel__row">
                      <div>
                        <div className="wdm-summary-card__total">
                          {formatCurrency(reportStats.totalElectronic)}
                        </div>
                        <div className="wdm-summary-card__label">
                          {te('totalPaiementsElectroniques')}
                        </div>
                      </div>
                      <div className="wdm-panel__pct-block">
                        <div className="wdm-summary-card__count">
                          {reportStats.electronicProviders.length}
                        </div>
                        <div className="wdm-summary-card__meta">
                          {te('methodePaiement')}
                        </div>
                      </div>
                    </div>
                    <div className="wdm-summary-card__meta">
                      {reportStats.electronicPercentage || 0}% {te('totalVentes2')}
                    </div>
                  </div>

                  <div className="wdm-table-wrap">
                    <table className="wdm-table wdm-table--2xl">
                      <thead>
                        <tr className="wdm-table__head-row">
                          <th className="wdm-table__th wdm-table__th--start">{te('provider')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('amount')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('transactions')}</th>
                          <th className="wdm-table__th wdm-table__th--num">{te('part')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportStats.electronicProviders.map((provider) => {
                          const percentage = reportStats.totalElectronic > 0
                            ? ((provider.totalAmount / reportStats.totalElectronic) * 100)
                            : 0;
                          const providerKey = String(provider.name || '').toLowerCase();
                          const displayName = providerKey === 'electronic'
                            ? te('unspecified')
                            : getPaymentMethodName(providerKey, currentLanguage);
                          return (
                            <tr key={provider.name} className="wdm-table__row">
                              <td className="wdm-table__cell wdm-table__cell--start wdm-table__cell--provider">{displayName}</td>
                              <td className="wdm-table__cell wdm-table__cell--amount">{formatCurrency(provider.totalAmount)}</td>
                              <td className="wdm-table__cell wdm-table__cell--num">{provider.count}</td>
                              <td className="wdm-table__cell wdm-table__cell--num">{percentage.toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <div>
              <h3 className="wdm-section-title">
                {te('mesNotifications')}
              </h3>
              {workerNotifications.length > 0 ? (
                <div className="wdm-stack--md">
                  {workerNotifications.map((notification, index) => (
                    <div key={index} className="wdm-card wdm-notification">
                      {notification.type === 'accepted' && <CheckCircle size={20} className="wdm-icon--green" />}
                      {notification.type === 'rejected' && <XCircle size={20} className="wdm-icon--red" />}
                      <div className="wdm-notification__body">
                        <div className="wdm-notification__title">
                          {notification.message}
                        </div>
                        <div className="wdm-notification__date">
                          {formatSafeDate(notification.timestamp, displayLocale, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }, '-')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="wdm-empty-state">
                  <Bell size={48} className="wdm-icon--empty" />
                  <p>{te('aucuneNotification')}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'deviations' && (
            <div>
              <h3 className="wdm-section-title">
                {te('mesEcarts')}
              </h3>
              <div className="wdm-stack--lg">
                {workerDeviations.self.map((deviation, index) => {
                  const diff = deviation.deviation?.difference || 0;
                  const isPositive = diff >= 0;
                  return (
                    <div key={`self-${index}`} className="wdm-card">
                      <div className="wdm-deviation__header">
                        <span className="wdm-deviation__type">
                          {te('typeSelf')}
                        </span>
                        <span className={`wdm-deviation__status ${deviationStatusClass(deviation.status)}`}>
                          {deviationStatusLabel(deviation.status)}
                        </span>
                      </div>
                      <div className={`wdm-deviation__amount ${isPositive ? 'wdm-deviation__amount--positive' : 'wdm-deviation__amount--negative'}`}>
                        <span>{isPositive ? '↗️' : '↘️'}</span>
                        <span>{te('amount')}: {isPositive ? '+' : ''}{formatCurrency(diff)}</span>
                      </div>
                      {deviation.explanation && (
                        <div className="wdm-deviation__explanation">
                          {te('explication')}: {deviation.explanation}
                        </div>
                      )}
                    </div>
                  );
                })}

                {workerDeviations.handover.map((deviation, index) => {
                  const diff = deviation.deviation?.difference || 0;
                  const isPositive = diff >= 0;
                  return (
                    <div key={`handover-${index}`} className="wdm-card">
                      <div className="wdm-deviation__header">
                        <span className="wdm-deviation__type">
                          {te('typeHandover')}
                        </span>
                        <span className={`wdm-deviation__status ${deviationStatusClass(deviation.status)}`}>
                          {deviationStatusLabel(deviation.status)}
                        </span>
                      </div>
                      <div className={`wdm-deviation__amount ${isPositive ? 'wdm-deviation__amount--positive' : 'wdm-deviation__amount--negative'}`}>
                        <span>{isPositive ? '↗️' : '↘️'}</span>
                        <span>{te('amount')}: {isPositive ? '+' : ''}{formatCurrency(diff)}</span>
                      </div>
                      {deviation.explanation && (
                        <div className="wdm-deviation__explanation">
                          {te('explication')}: {deviation.explanation}
                        </div>
                      )}
                    </div>
                  );
                })}

                {workerDeviations.total === 0 && (
                  <div className="wdm-empty-state">
                    <CheckCircle size={48} className="wdm-icon--empty" />
                    <p>{te('aucunEcart')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </AdsModalPortal>
  );
};

export default WorkerDashboardModal;
